import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { environment } from 'src/environments/environment';
import { EnvironmentConfigService } from 'src/app/services/environment-config-service/environment-config.service';

const phoneUtil = PhoneNumberUtil.getInstance();

/** Normalize to E.164 without spaces; empty input returns null. */
export function normalizeMobileNumberE164(value: string | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  let v = value.trim().replace(/\s+/g, '');
  if (!v) {
    return null;
  }
  if (!v.startsWith('+')) {
    v = `+${v.replace(/^\+/, '')}`;
  }
  v = v.charAt(0) + v.slice(1).replace(/\D/g, '');
  return v.length > 1 ? v : null;
}

/** Validate E.164 number; empty is valid (optional field). */
export function isValidMobileNumberE164(e164: string | null | undefined, regionIso?: string): boolean {
  const normalized = normalizeMobileNumberE164(e164);
  if (!normalized) {
    return true;
  }
  try {
    const parsed = normalized.startsWith('+')
      ? phoneUtil.parse(normalized, '')
      : phoneUtil.parse(normalized, (regionIso ?? 'ZA').toUpperCase());
    return phoneUtil.isValidNumber(parsed);
  } catch {
    return false;
  }
}

function tryParseNationalToE164(national: string, regionIso: string): string | null {
  try {
    const parsed = phoneUtil.parse(national, regionIso.toUpperCase());
    if (!phoneUtil.isValidNumber(parsed)) {
      return null;
    }
    return phoneUtil.format(parsed, PhoneNumberFormat.E164);
  } catch {
    return null;
  }
}

/** Parse national number for a region and return E.164, or null if invalid. */
export function formatToE164(nationalNumber: string, regionIso: string): string | null {
  const trimmed = (nationalNumber ?? '').trim();
  if (!trimmed) {
    return null;
  }
  const region = regionIso.toUpperCase();
  const direct = tryParseNationalToE164(trimmed, region);
  if (direct) {
    return direct;
  }
  // Many regions store mobiles with a trunk prefix (e.g. 082…) while users omit the leading 0.
  if (!trimmed.startsWith('0') && /^\d+$/.test(trimmed)) {
    return tryParseNationalToE164(`0${trimmed}`, region);
  }
  return null;
}

/** Build E.164 from national digits and dial code even when libphonenumber rejects it (for blur validation). */
export function formatAttemptedE164(nationalDigits: string, dialCode: string): string | null {
  const digits = (nationalDigits ?? '').replace(/\D/g, '');
  const dial = (dialCode ?? '').replace(/\D/g, '');
  if (!digits || !dial) {
    return null;
  }
  return normalizeMobileNumberE164(`+${dial}${digits}`);
}

/** Optional mobile: empty valid; non-empty must be valid E.164. */
export function optionalMobileNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw == null || String(raw).trim() === '') {
      return null;
    }
    return isValidMobileNumberE164(String(raw)) ? null : { invalidMobile: true };
  };
}

export const BOTSWANA_COUNTRY_ISO = 'BW';
export const BOTSWANA_ONLY_COUNTRIES = ['bw'] as const;

/** Validate E.164 number is a valid Botswana mobile; empty is valid (optional field). */
export function isValidBotswanaMobileNumberE164(e164: string | null | undefined): boolean {
  const normalized = normalizeMobileNumberE164(e164);
  if (!normalized) {
    return true;
  }
  if (!isValidMobileNumberE164(normalized)) {
    return false;
  }
  try {
    const parsed = phoneUtil.parse(normalized, '');
    return phoneUtil.getRegionCodeForNumber(parsed) === BOTSWANA_COUNTRY_ISO;
  } catch {
    return false;
  }
}

/** Optional mobile restricted to Botswana numbers. */
export function optionalBotswanaMobileNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw == null || String(raw).trim() === '') {
      return null;
    }
    return isValidBotswanaMobileNumberE164(String(raw)) ? null : { invalidMobile: true };
  };
}

/**
 * Default country for phone input: build-time `defaultMobileCountryCode`, then CurrentCountry().
 * When neither is set (e.g. dev `countryCode` undefined), returns undefined.
 */
export async function resolveDefaultCountryIso(
  envConfig?: EnvironmentConfigService
): Promise<string | undefined> {
  const mobileCountry = (environment as { defaultMobileCountryCode?: string }).defaultMobileCountryCode;
  if (mobileCountry) {
    return mobileCountry.toUpperCase();
  }
  if (envConfig) {
    const current = await envConfig.CurrentCountry();
    if (current?.countryCode) {
      return current.countryCode.toUpperCase();
    }
  }
  return undefined;
}

/** Parse stored E.164 for display in intl-tel-input. */
export function parseE164ForDisplay(e164: string | null | undefined): { number: string; countryIso?: string } | null {
  const normalized = normalizeMobileNumberE164(e164);
  if (!normalized) {
    return null;
  }
  try {
    const parsed = phoneUtil.parse(normalized, undefined as unknown as string);
    const countryIso = phoneUtil.getRegionCodeForNumber(parsed);
    return { number: normalized, countryIso: countryIso ?? undefined };
  } catch {
    return { number: normalized };
  }
}
